// SGEW 18 Oct 2026 (owner: "like the Items page, if anything is going low we highlight a red background - on the other
// pages too, based on the page"): when a value needs attention - stock at or below its minimum, a date that has passed
// while the work is still open, a request waiting too long. Days are counted on the Indian calendar: the server's dates
// are Indian time and are read as written (utils/format), and "today" is today in India whatever zone the browser is in.
// The pill that shows it is components/common/StatusPill.

/** A request waiting more than this many days is shown red (approvals, dealer requests, warranty claims). */
export const WAIT_DAYS = 3;

// a sale / service is late only while it is still open: waiting for approval, placed / started or processed (a quotation
// promises nothing; delivered, completed and cancelled ones are done)
export const OPEN_ORDER_STATUSES = ["Pending", "Order Placed", "Order Processed"];
export const OPEN_SERVICE_STATUSES = ["Pending", "Service Initiated", "Service Processed"];

const INDIA = new Intl.DateTimeFormat("en-GB", { timeZone: "Asia/Kolkata", year: "numeric", month: "2-digit", day: "2-digit" });

/** Today in India as "YYYY-MM-DD". */
export const todayIndia = (now = new Date()) => {
  const p = Object.fromEntries(INDIA.formatToParts(now).map((x) => [x.type, x.value]));
  return `${p.year}-${p.month}-${p.day}`;
};

// "YYYY-MM-DD[ HH:MM:SS]" -> a day number (days since 1970-01-01), null for nothing / "0000-00-00"
const dayNumber = (v) => {
  const m = String(v ?? "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m || m[1] === "0000") return null;
  return Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) / 86400000;
};

/** Calendar days from the date v to today in India: 0 today, 1 yesterday, negative for a day ahead; null without a date. */
export const daysAgo = (v, now) => {
  const d = dayNumber(v);
  return d === null ? null : dayNumber(todayIndia(now)) - d;
};

/** Days the date v is past (0 when it has not passed yet or there is no date). */
export const daysLate = (v, now) => Math.max(0, daysAgo(v, now) ?? 0);

/** "1 day", "4 days" */
export const dayCount = (n) => `${n} day${Math.abs(n) === 1 ? "" : "s"}`;

/** "Today", "1 day", "5 days" - how long something has waited since v ("" without a date). */
export const waitedFor = (v, now) => {
  const n = daysAgo(v, now);
  if (n === null) return "";
  return n <= 0 ? "Today" : dayCount(n);
};

/** True when something raised on v has waited more than WAIT_DAYS days. */
export const waitingTooLong = (v, now) => (daysAgo(v, now) ?? 0) > WAIT_DAYS;

/**
 * Stock at or below its minimum (the dashboard's stock alerts and the Low stock filters use the same rule). Without a
 * minimum only an empty stock (0 or less) is low.
 */
export const isLowStock = (stock, min) => {
  const s = Number(stock || 0);
  const hasMin = min !== null && min !== undefined && min !== "" && Number.isFinite(Number(min));
  return hasMin ? s <= Number(min) : s <= 0;
};

/** Why a stock figure is red / what its minimum is: "Out of stock", "Below minimum 10", "At minimum 10", "Minimum 10". */
export const stockReason = (stock, min) => {
  const s = Number(stock || 0);
  const hasMin = min !== null && min !== undefined && min !== "" && Number.isFinite(Number(min));
  const m = hasMin ? Number(min) : null;
  if (s <= 0) return m > 0 ? `Out of stock (minimum ${m})` : "Out of stock";
  if (m === null) return "In stock";
  if (s < m) return `Below minimum ${m}`;
  if (s === m) return `At minimum ${m}`;
  return `Minimum ${m}`;
};
